import React from "react";

/**
 * Per-portion attribution for an element-year.
 *
 * An element carrying a damage split (`C3A-93;C5B-7`) degrades as independent
 * portions, each on its own exposure class's curve. The element-level
 * degradation shown elsewhere is the dominant portion's, so a repair driven by
 * a small, faster-corroding portion looks as though it fired below its own
 * threshold. This table shows which portion actually triggered.
 *
 * Renders nothing for single-portion elements — the element-level figures
 * already describe those exactly.
 */

const REPAIR_LABELS = {
  TU: "Touch-up",
  OC: "Overcoat",
  RE: "Recoat",
  WASH: "Wash",
};

export const formatRepairLabel = (label) =>
  REPAIR_LABELS[label] || label || "—";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// The threshold a portion is being measured against is the one for the action
// it triggered; before it triggers, the next one it is heading for is touch-up.
const relevantThreshold = (portion) => {
  const thresholds = portion?.thresholds || {};
  if (portion?.repairType === "RE") return thresholds.recoat;
  if (portion?.repairType === "OC") return thresholds.overcoat;
  return thresholds.touchup;
};

const DamagePortionsBreakdown = ({ portions, className = "" }) => {
  if (!Array.isArray(portions) || portions.length < 2) return null;

  return (
    <div className={className}>
      <h4 className="font-semibold text-gray-900 mb-2">Damage Portions</h4>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border rounded">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="text-left px-2 py-1 font-medium">Combination</th>
              <th className="text-right px-2 py-1 font-medium">Share</th>
              <th className="text-right px-2 py-1 font-medium">Degradation</th>
              <th className="text-right px-2 py-1 font-medium">Threshold</th>
              <th className="text-left px-2 py-1 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {portions.map((portion, index) => {
              const threshold = relevantThreshold(portion);
              return (
                <tr
                  key={`${portion.combinationCode || "portion"}-${index}`}
                  className={
                    portion.triggered
                      ? "bg-amber-50 text-gray-900"
                      : "text-gray-600"
                  }
                >
                  <td className="px-2 py-1 font-mono">
                    {portion.combinationCode || "N/A"}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {toNumber(portion.percentage).toFixed(0)}%
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {toNumber(portion.degradationPercent).toFixed(3)}%
                  </td>
                  <td className="px-2 py-1 text-right font-mono">
                    {threshold == null ? "—" : `${toNumber(threshold)}%`}
                  </td>
                  <td className="px-2 py-1">
                    {portion.triggered ? (
                      <span className="inline-flex items-center rounded bg-amber-200 text-amber-900 px-1.5 py-0.5 font-medium">
                        {formatRepairLabel(portion.repairType)}
                      </span>
                    ) : (
                      <span className="text-gray-400">below threshold</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Each portion degrades on its own exposure class&apos;s curve, so the
        element&apos;s degradation above is the dominant portion&apos;s, not
        necessarily the one that triggered.
      </p>
    </div>
  );
};

export default DamagePortionsBreakdown;
